import React, { createContext, useContext, useState, useCallback } from "react";
import useWidgetImagesPreload from "./hooks/useWidgetImagesPreload";
import { IUserContextValue } from "./types";

const INIT_VALUE: IUserContextValue = {
  reset: () => {},
  shouldShowLoggedOutOverlay: false,
  hasIdentifiedByErrorReporter: false,
  identifyByErrorReporter: () => {},
  updateShouldShowLoggedOutOverlay: (newState: boolean) => {},
  preloadWidgetImage: () => {},
};

const UserContext = createContext(INIT_VALUE);

const withUserContext = <T extends object>(
  Component: React.FunctionComponent<T>
) => {
  const WithUserContext = (props: T) => {
    const [hasIdentifiedByErrorReporter, setHasIdentifiedByErrorReporter] =
      useState(false);
    const [shouldShowLoggedOutOverlay, setShouldShowLoggedOutOverlay] =
      useState(false);

    const { preloadWidgetImage, reset: resetWidgetImagesPreload } =
      useWidgetImagesPreload();

    const _resetUserContext = useCallback(() => {
      setHasIdentifiedByErrorReporter(false);
      setShouldShowLoggedOutOverlay(false);
      resetWidgetImagesPreload();
    }, [resetWidgetImagesPreload]);

    const _identifyByErrorReporter = useCallback(() => {
      setHasIdentifiedByErrorReporter(true);
    }, []);

    return (
      <UserContext.Provider
        value={{
          reset: _resetUserContext,
          shouldShowLoggedOutOverlay,
          hasIdentifiedByErrorReporter,
          identifyByErrorReporter: _identifyByErrorReporter,
          updateShouldShowLoggedOutOverlay: setShouldShowLoggedOutOverlay,
          preloadWidgetImage,
        }}
      >
        <Component {...props} />
      </UserContext.Provider>
    );
  };

  return WithUserContext;
};

export default withUserContext;

export const useUserContext = () => useContext(UserContext);
